import React, { useState } from 'react';
import {
  FiMessageCircle as MessageCircle,
  FiPhone as Phone,
  FiSend as Send,
  FiX as X,
} from 'react-icons/fi';
import { supportAPI } from '../services/realApi';

interface SupportRequestProps {
  assignee?: string;
  onClose: () => void;
}

interface RequestType {
  value: string;
  label: string;
}

interface PriorityLevel {
  value: string;
  label: string;
  color: string;
}

const SupportRequest: React.FC<SupportRequestProps> = ({ onClose }) => {
  const [requestType, setRequestType] = useState('');
  const [priority, setPriority] = useState('medium');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('aivilo');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await supportAPI.submitSupportRequest({
        requestType,
        priority,
        subject,
        description,
        supportChannel: 'portal',
        assignee: selectedAssignee,
      });

      if (result.success) {
        const assigneeName = selectedAssignee === 'aivilo' ? 'Aivilo' : 'AWS';
        const estimatedResponse = (result.data as { estimatedResponse?: string })?.estimatedResponse ?? '24 hours';
        alert(`Support request submitted successfully! ${assigneeName} will respond within ${estimatedResponse}.`);
        onClose();
      } else {
        alert(`Failed to submit support request: ${result.error}`);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert(`Error submitting support request: ${message}`);
    }

    setIsSubmitting(false);
  };

  const requestTypes: RequestType[] = [
    { value: 'technical', label: 'Technical Issue' },
    { value: 'billing', label: 'Billing Question' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'bug_report', label: 'Bug Report' },
  ];

  const priorityLevels: PriorityLevel[] = [
    { value: 'low', label: 'Low', color: 'text-green-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-blue-600' },
    { value: 'critical', label: 'Critical', color: 'text-red-600' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 transition-colors">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">Request Support</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Support Channel Selection */}
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 transition-colors">
              Who would you like to contact?
            </span>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setSelectedAssignee('aivilo')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedAssignee === 'aivilo'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900 dark:text-white transition-colors">Aivilo Support</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
                      Project &amp; technical support
                    </div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedAssignee('aws')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  selectedAssignee === 'aws'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Phone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <div className="text-left">
                    <div className="font-medium text-gray-900 dark:text-white transition-colors">AWS Representative</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 transition-colors">
                      AWS services &amp; account support
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Request Type */}
          <div>
            <label htmlFor="request-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Request Type
            </label>
            <select id="request-type"
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <option value="">Select a request type</option>
              {requestTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Priority Level
            </span>
            <div className="grid grid-cols-4 gap-2">
              {priorityLevels.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => setPriority(level.value)}
                  className={`p-2 text-sm font-medium rounded-md transition-all ${
                    priority === level.value
                      ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Subject
            </label>
            <input id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              placeholder="Brief description of your request"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
              Description
            </label>
            <textarea id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              placeholder="Please provide detailed information about your request..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportRequest;
